import { Link } from "@tanstack/react-router";
import { ArrowLeft, Globe } from "lucide-react";
import type { BlogPost } from "@/lib/blog";

interface Props {
  post: BlogPost;
  alternatePost?: BlogPost;
  lang: "en" | "es";
}

export function BlogPostLayout({ post, alternatePost, lang }: Props) {
  const isEs = lang === "es";
  const backTo = isEs ? "/es/blog" : "/blog";

  return (
    <main className="max-w-3xl mx-auto px-6 py-16">
      {/* Nav row */}
      <div className="flex items-center gap-4 mb-10">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {isEs ? "Volver al blog" : "Back to blog"}
        </Link>
        {alternatePost && (
          <Link
            to={isEs ? "/blog/$slug" : "/es/blog/$slug"}
            params={{ slug: alternatePost.slug }}
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-primary hover:opacity-80 transition-opacity"
          >
            <Globe className="w-3.5 h-3.5" />
            {isEs ? "Read in English" : "Leer en español"}
          </Link>
        )}
      </div>

      <article>
        <header className="mb-10">
          <time className="text-xs text-muted-foreground uppercase tracking-wide">
            {new Date(post.date + "T00:00:00").toLocaleDateString(
              isEs ? "es-ES" : "en-US",
              { year: "numeric", month: "long", day: "numeric" },
            )}
          </time>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold leading-tight text-foreground">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            {post.description}
          </p>
        </header>

        {post.ogImage && (
          <img
            src={post.ogImage}
            alt={post.title}
            className="w-full rounded-2xl mb-10 aspect-video object-cover"
          />
        )}

        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: post.contentHtml }}
        />
      </article>

      {/* Footer author line */}
      <footer className="mt-16 pt-8 border-t border-border/40 flex items-center justify-between flex-wrap gap-4">
        <p className="text-sm text-muted-foreground">
          {isEs ? "Escrito por" : "By"}{" "}
          <span className="text-foreground font-medium">AdKavi</span>
        </p>
        {alternatePost && (
          <Link
            to={isEs ? "/blog/$slug" : "/es/blog/$slug"}
            params={{ slug: alternatePost.slug }}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:opacity-80 transition-opacity"
          >
            <Globe className="w-3.5 h-3.5" />
            {isEs ? "Read in English" : "Leer en español"}
          </Link>
        )}
      </footer>
    </main>
  );
}
